// نشست مستأجر (C15، keycloak-setup §۷): me/tenants ← select-tenant ← توکن مستأجر ES256 (۵ دقیقه، فقط در حافظه)
// ← تمدید خودکار با همان Endpoint پیش از انقضا. جابه‌جایی مستأجر بافت کلاینت را کاملاً پاک می‌کند (AC-IAM-002.3).
import {
  ApiError,
  createPhoenixClient,
  ErrorCodes,
  isApiError,
  unwrap,
  type MyTenant,
  type PhoenixClient,
  type TenantSession,
} from '@phoenix/api-client';
import { acceptLanguage, useTranslation } from '@phoenix/i18n';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from './AuthProvider';

export type TenantSessionStatus =
  'loading' | 'choose' | 'no-membership' | 'selecting' | 'active' | 'suspended' | 'error';

export interface ApiNotices {
  /** آخرین 423 (PHX-PLT-0423) — بنر حالت دسترسی. */
  locked: ApiError | null;
  /** آخرین 403 PHX-PLT-0403 — پیشنهاد ارتقا. */
  featureDisabled: ApiError | null;
}

export interface TenantSessionValue {
  status: TenantSessionStatus;
  tenants: readonly MyTenant[];
  session: TenantSession | null;
  activeTenant: MyTenant | null;
  /** خطای انتخاب مستأجر (PHX-IAM-0103، PHX-IAM-0104، 423). */
  selectError: ApiError | null;
  /** خطای دریافت فهرست مستأجرها. */
  loadError: unknown;
  /** با هر جابه‌جایی مستأجر افزایش می‌یابد؛ کلید React محتوا ← ریست کامل فرم‌ها و وضعیت. */
  epoch: number;
  /** کلاینت با توکن Keycloak (me، me/tenants، select-tenant، Console). */
  keycloakApi: PhoenixClient;
  /** کلاینت با توکن مستأجر (همه Endpointهای سطح مستأجر). */
  tenantApi: PhoenixClient;
  notices: ApiNotices;
  dismissFeatureNotice: () => void;
  /** انتخاب اولیه از صفحه SCR-STAFF-001. */
  selectTenant: (tenantId: string, activeBranchId?: string | null) => Promise<boolean>;
  /** جابه‌جایی مستأجر از منوی حساب: پاک‌سازی Cache، ریست فرم‌ها و رفتن به خانه (AC-IAM-002.3). */
  switchTenant: (tenantId: string, activeBranchId?: string | null) => Promise<boolean>;
  /** تغییر شعبه فعال (AC-IAM-002.2): توکن تازه با active_branch_id و بازخوانی داده‌ها. */
  switchBranch: (branchId: string) => Promise<boolean>;
  /** قفل صفحه: حذف توکن مستأجر از حافظه و توقف تمدید. */
  suspend: () => void;
  /** پس از ورود مجدد: صدور دوباره توکن همان مستأجر و شعبه. */
  resume: () => Promise<boolean>;
  reload: () => void;
  /** ثبت پاک‌سازی اضافه هنگام جابه‌جایی مستأجر (مثلاً قطع SignalR در آینده). */
  onReset: (handler: () => void) => () => void;
}

/** Context نشست مستأجر (صادر برای نشست حساب دستگاه — AP-IAM-05). */
export const TenantSessionContext = createContext<TenantSessionValue | null>(null);

export interface TenantSessionProviderProps {
  children: ReactNode;
  baseUrl?: string;
  /** انتخاب خودکار وقتی فقط یک مستأجر هست (پیش‌فرض true — AC-IAM-001.1). */
  autoSelectSingle?: boolean;
  /** چند ثانیه پیش از انقضا توکن مستأجر تمدید شود (پیش‌فرض ۶۰). */
  refreshSkewSeconds?: number;
  /** fetch سفارشی (تست). */
  fetch?: typeof globalThis.fetch;
}

interface TokenState {
  token: string;
  expiresAtMs: number;
  tenantId: string;
  activeBranchId: string | null;
}

/** فاصله تلاش دوباره تمدید توکن مستأجر هنگام قطع شبکه. */
const OFFLINE_RETRY_MS = 15_000;

function expiresAtMs(session: TenantSession): number {
  const parsed = Date.parse(session.expiresAt);
  if (!Number.isNaN(parsed)) {
    return parsed;
  }
  return Date.now() + Number(session.expiresIn) * 1000;
}

export function TenantSessionProvider({
  children,
  baseUrl = '',
  autoSelectSingle = true,
  refreshSkewSeconds = 60,
  fetch: customFetch,
}: TenantSessionProviderProps) {
  const auth = useAuth();
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [status, setStatus] = useState<TenantSessionStatus>('loading');
  const [tenants, setTenants] = useState<readonly MyTenant[]>([]);
  const [session, setSession] = useState<TenantSession | null>(null);
  const [selectError, setSelectError] = useState<ApiError | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [epoch, setEpoch] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);
  const [notices, setNotices] = useState<ApiNotices>({ locked: null, featureDisabled: null });

  const tokenRef = useRef<TokenState | null>(null);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshing = useRef<Promise<boolean> | null>(null);
  const resetHandlers = useRef(new Set<() => void>());
  /** در حالت قفل هیچ توکن مستأجری صادر یا ارسال نمی‌شود (BR-IAM-050). */
  const suspendedRef = useRef(false);
  const languageRef = useRef(i18n.language);
  languageRef.current = i18n.language;

  const getLanguage = useCallback(() => acceptLanguage(languageRef.current), []);

  const keycloakApi = useMemo(
    () =>
      createPhoenixClient({
        baseUrl,
        fetch: customFetch,
        getLanguage,
        getToken: (options) => auth.getAccessToken(options),
        onUnauthorized: async () => true,
      }),
    // auth.getAccessToken به client وابسته است و پایدار می‌ماند.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseUrl, customFetch, getLanguage],
  );

  const clearTimer = () => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  };

  const issue = useCallback(
    async (tenantId: string, activeBranchId: string | null): Promise<TenantSession> =>
      unwrap(
        keycloakApi.POST('/api/v1/identity/sessions:select-tenant', { body: { tenantId, activeBranchId } }),
      ),
    [keycloakApi],
  );

  const applySession = useCallback(
    (next: TenantSession) => {
      tokenRef.current = {
        token: next.accessToken,
        expiresAtMs: expiresAtMs(next),
        tenantId: next.tenantId,
        activeBranchId: next.activeBranchId,
      };
      setSession(next);
      clearTimer();
      const delay = Math.max(5_000, tokenRef.current.expiresAtMs - Date.now() - refreshSkewSeconds * 1000);
      refreshTimer.current = setTimeout(() => {
        void refreshRef.current();
      }, delay);
    },
    [refreshSkewSeconds],
  );

  /** تمدید توکن مستأجر با همان Endpoint (keycloak-setup §۷). */
  const refresh = useCallback(async (): Promise<boolean> => {
    const current = tokenRef.current;
    if (!current || suspendedRef.current) {
      return false;
    }
    refreshing.current ??= (async () => {
      try {
        applySession(await issue(current.tenantId, current.activeBranchId));
        return true;
      } catch (error) {
        if (isApiError(error) && error.status === 0) {
          // AP-OFF-01 (ARD-offline §۴، C-AGENT-1548): قطع شبکه نشست را نمی‌بندد؛ توکن منقضی در حافظه می‌ماند (درخواست‌ها به‌هرحال به شبکه
          // نمی‌رسند) و تمدید هر ۱۵ ثانیه تا اتصال تکرار می‌شود. پایان نشست فقط با پاسخ واقعی سرور یا کیکلاک است.
          clearTimer();
          refreshTimer.current = setTimeout(() => {
            void refreshRef.current();
          }, OFFLINE_RETRY_MS);
          return false;
        }
        tokenRef.current = null;
        clearTimer();
        setSession(null);
        if (isApiError(error) && error.status !== 401 && error.status !== 0) {
          setSelectError(error);
          setStatus('choose');
        } else if (isApiError(error) && error.status === 0) {
          setStatus('error');
          setLoadError(error);
        } else {
          setStatus('loading');
          await auth.login();
        }
        return false;
      } finally {
        refreshing.current = null;
      }
    })();
    return refreshing.current;
  }, [applySession, issue, auth]);

  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  const tenantApi = useMemo(
    () =>
      createPhoenixClient({
        baseUrl,
        fetch: customFetch,
        getLanguage,
        getToken: async (options) => {
          if (suspendedRef.current) {
            return undefined;
          }
          if (
            options?.forceRefresh ||
            (tokenRef.current && tokenRef.current.expiresAtMs - Date.now() < 5_000)
          ) {
            await refreshRef.current();
          }
          return tokenRef.current?.token;
        },
        onUnauthorized: async (error) =>
          !suspendedRef.current &&
          tokenRef.current !== null &&
          (error.code === ErrorCodes.membershipStale || error.code === ErrorCodes.unauthenticated),
        onLocked: (error) => {
          setNotices((value) => ({ ...value, locked: error }));
          void queryClient.invalidateQueries({ queryKey: ['identity', 'me', 'access'] });
        },
        onFeatureDisabled: (error) => setNotices((value) => ({ ...value, featureDisabled: error })),
      }),
    [baseUrl, customFetch, getLanguage, queryClient],
  );

  const doSelect = useCallback(
    async (tenantId: string, activeBranchId: string | null): Promise<boolean> => {
      setStatus('selecting');
      setSelectError(null);
      try {
        applySession(await issue(tenantId, activeBranchId));
        setNotices({ locked: null, featureDisabled: null });
        setStatus('active');
        return true;
      } catch (error) {
        setSelectError(isApiError(error) ? error : new ApiError({ status: 0, code: ErrorCodes.network }));
        setStatus('choose');
        return false;
      }
    },
    [applySession, issue],
  );

  // بارگذاری فهرست مستأجرها پس از ورود و انتخاب خودکار تک‌مستأجر (AC-IAM-001.1، AC-IAM-001.2).
  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setLoadError(null);
    unwrap(keycloakApi.GET('/api/v1/identity/me/tenants'))
      .then(async (result) => {
        if (cancelled) {
          return;
        }
        const items = result.items;
        setTenants(items);
        if (items.length === 0) {
          setStatus('no-membership');
        } else if (items.length === 1 && autoSelectSingle && items[0]) {
          await doSelect(items[0].tenantId, null);
        } else {
          setStatus('choose');
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setLoadError(error);
          setStatus('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [keycloakApi, autoSelectSingle, doSelect, reloadToken]);

  useEffect(() => () => clearTimer(), []);

  const resetClientContext = useCallback(() => {
    void queryClient.cancelQueries();
    queryClient.clear();
    resetHandlers.current.forEach((handler) => handler());
    setEpoch((value) => value + 1);
  }, [queryClient]);

  const selectTenant = useCallback(
    async (tenantId: string, activeBranchId: string | null = null) => {
      const ok = await doSelect(tenantId, activeBranchId);
      if (ok) {
        navigate('/', { replace: true });
      }
      return ok;
    },
    [doSelect, navigate],
  );

  const switchTenant = useCallback(
    async (tenantId: string, activeBranchId: string | null = null) => {
      // AC-IAM-002.3: هیچ داده مستأجر قبلی در نشست تازه نمی‌ماند.
      clearTimer();
      tokenRef.current = null;
      setSession(null);
      resetClientContext();
      const ok = await doSelect(tenantId, activeBranchId);
      navigate(ok ? '/' : '/select-tenant', { replace: true });
      return ok;
    },
    [doSelect, navigate, resetClientContext],
  );

  const switchBranch = useCallback(
    async (branchId: string) => {
      const current = tokenRef.current;
      if (!current) {
        return false;
      }
      try {
        applySession(await issue(current.tenantId, branchId));
        await queryClient.invalidateQueries();
        return true;
      } catch (error) {
        setSelectError(isApiError(error) ? error : null);
        return false;
      }
    },
    [applySession, issue, queryClient],
  );

  const suspend = useCallback(() => {
    suspendedRef.current = true;
    clearTimer();
    if (tokenRef.current) {
      tokenRef.current = { ...tokenRef.current, token: '', expiresAtMs: 0 };
    }
    void queryClient.cancelQueries();
    setStatus('suspended');
  }, [queryClient]);

  const resume = useCallback(async () => {
    const current = tokenRef.current;
    if (!current) {
      return false;
    }
    suspendedRef.current = false;
    try {
      applySession(await issue(current.tenantId, current.activeBranchId));
      setStatus('active');
      return true;
    } catch (error) {
      setSelectError(isApiError(error) ? error : null);
      setStatus('choose');
      return false;
    }
  }, [applySession, issue]);

  const onReset = useCallback((handler: () => void) => {
    resetHandlers.current.add(handler);
    return () => {
      resetHandlers.current.delete(handler);
    };
  }, []);

  const activeTenant = useMemo(
    () => tenants.find((tenant) => tenant.tenantId === session?.tenantId) ?? null,
    [tenants, session],
  );

  const value = useMemo<TenantSessionValue>(
    () => ({
      status,
      tenants,
      session,
      activeTenant,
      selectError,
      loadError,
      epoch,
      keycloakApi,
      tenantApi,
      notices,
      dismissFeatureNotice: () => setNotices((current) => ({ ...current, featureDisabled: null })),
      selectTenant,
      switchTenant,
      switchBranch,
      suspend,
      resume,
      reload: () => setReloadToken((n) => n + 1),
      onReset,
    }),
    [
      status,
      tenants,
      session,
      activeTenant,
      selectError,
      loadError,
      epoch,
      keycloakApi,
      tenantApi,
      notices,
      selectTenant,
      switchTenant,
      switchBranch,
      suspend,
      resume,
      onReset,
    ],
  );

  return <TenantSessionContext.Provider value={value}>{children}</TenantSessionContext.Provider>;
}

export function useTenantSession(): TenantSessionValue {
  const context = useContext(TenantSessionContext);
  if (!context) {
    throw new Error('useTenantSession must be used inside <TenantSessionProvider>.');
  }
  return context;
}

/** نسخه بدون خطا (برای اجزای مشترک بین staff و console). */
export function useOptionalTenantSession(): TenantSessionValue | null {
  return useContext(TenantSessionContext);
}

/** کلاینت API مستأجر جاری. */
export function useTenantApi(): PhoenixClient {
  return useTenantSession().tenantApi;
}
